import { Check, Eye, ShieldCheck } from 'lucide-react';
import type { OrgTreeNode, UserMembership } from '@vtak/shared';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useMembershipQuery } from '@/queries/use-membership-query';
import { useOrgTreesQuery } from '@/queries/use-org-trees-query';
import { switchWorkspace } from '@/lib/workspace-switch';
import { flattenTree } from '@/lib/org-tree';
import { SettingsMyView } from './settings-my-view';
import { resolveEmblem } from '@/constants/emblems';
import { cn } from '@/lib/utils';

/**
 * Settings panel. The View section shows the active unit and lets the
 * operator switch between their own units; admins additionally get a
 * "preview as unit" switcher over the full org tree (server-authorized by
 * inherited admin role — units outside their span fall back to their own view).
 */
export function SettingsPanel(): React.JSX.Element {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const membershipQuery = useMembershipQuery();
  const approved = (membershipQuery.data ?? []).filter((m) => m.status === 'approved');
  const isAdmin = approved.some((m) => m.role === 'admin');
  const previewing =
    !!workspace && approved.length > 0 && !approved.some((m) => m.unitId === workspace.unitId);
  const treesQuery = useOrgTreesQuery(isAdmin);

  return (
    <ScrollArea className="flex-1">
      <div className="flex flex-col gap-4 p-3">
        <section>
          <SectionLabel text="Active view" />
          <ActiveViewCard previewing={previewing} />
        </section>

        <SettingsMyView />

        <section>
          <SectionLabel text="My units" />
          <div className="flex flex-col gap-1">
            {approved.map((m) => (
              <MembershipRow
                key={m.unitId}
                membership={m}
                isActive={workspace?.unitId === m.unitId}
              />
            ))}
            {approved.length === 0 && (
              <p className="px-2 py-1 text-xs text-muted-foreground">No unit memberships.</p>
            )}
          </div>
        </section>

        {isAdmin && (
          <section>
            <SectionLabel text="Preview as unit" />
            <p className="mb-2 px-2 text-[11px] leading-relaxed text-muted-foreground/80">
              See the workspace another unit sees. Available for units under your
              admin span.
            </p>
            <div className="flex flex-col gap-0.5">
              {(treesQuery.data ?? []).flatMap((org) =>
                org.tree.flatMap((root) => flattenTree(root, 0)),
              ).map(({ node, depth }) => (
                <TreeUnitRow
                  key={node.id}
                  node={node}
                  depth={depth}
                  isActive={workspace?.unitId === node.id}
                />
              ))}
              {treesQuery.isLoading && (
                <p className="px-2 py-1 text-xs text-muted-foreground">Loading org tree…</p>
              )}
            </div>
          </section>
        )}
      </div>
    </ScrollArea>
  );
}

/** Chakra eyebrow label for a settings section */
function SectionLabel({ text }: { text: string }): React.JSX.Element {
  return (
    <p className="mb-1.5 px-2 font-chakra text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
      {text}
    </p>
  );
}

/** Card describing the workspace currently rendered */
function ActiveViewCard({ previewing }: { previewing: boolean }): React.JSX.Element {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const role = useWorkspaceStore((s) => s.role);
  const emblem = useWorkspaceStore((s) => s.emblem);
  const orgPath = useWorkspaceStore((s) => s.orgPath);

  if (!workspace) {
    return <p className="px-2 py-1 text-xs text-muted-foreground">No active unit.</p>;
  }

  const emblemUrl = resolveEmblem(emblem);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-echo-border bg-white/[0.03] px-3 py-2.5">
      <UnitEmblem emblemUrl={emblemUrl} shortName={workspace.shortName} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-chakra text-sm font-bold uppercase tracking-wider text-foreground">
            {workspace.shortName}
          </span>
          {previewing ? (
            <span className="flex items-center gap-1 rounded bg-echo-yellow/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-echo-yellow">
              <Eye className="h-2.5 w-2.5" />
              Preview
            </span>
          ) : (
            role && (
              <span className="rounded bg-echo-border/50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                {role}
              </span>
            )
          )}
        </div>
        <p className="truncate text-[11px] text-muted-foreground">{workspace.unitName}</p>
        {orgPath.length > 0 && (
          <p className="truncate text-[10px] font-medium text-muted-foreground/80">
            {orgPath.join(' › ')}
          </p>
        )}
      </div>
    </div>
  );
}

/** A unit the user belongs to; clicking switches the workspace to it. */
function MembershipRow({
  membership,
  isActive,
}: {
  membership: UserMembership;
  isActive: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={isActive}
      onClick={() => switchWorkspace(membership.unitId)}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
        isActive ? 'bg-echo-yellow/10' : 'hover:bg-white/[0.06]',
      )}
    >
      <UnitEmblem emblemUrl={resolveEmblem(membership.emblem ?? null)} shortName={membership.shortName} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{membership.shortName}</p>
        <p className="truncate text-[11px] text-muted-foreground">{membership.unitName}</p>
      </div>
      {membership.role === 'admin' && (
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
      )}
      {isActive && <Check className="h-4 w-4 shrink-0 text-echo-yellow" />}
    </button>
  );
}

/** An org-tree unit row, indented by echelon depth. */
function TreeUnitRow({
  node,
  depth,
  isActive,
}: {
  node: OrgTreeNode;
  depth: number;
  isActive: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={isActive}
      onClick={() => switchWorkspace(node.id)}
      style={{ paddingLeft: 8 + depth * 14 }}
      className={cn(
        'flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left transition-colors',
        isActive ? 'bg-echo-yellow/10' : 'hover:bg-white/[0.06]',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-foreground">{node.shortName}</p>
        <p className="truncate text-[10px] text-muted-foreground/80">{node.name}</p>
      </div>
      <span className="shrink-0 font-mono text-[9px] uppercase tracking-wide text-muted-foreground/60">
        {node.echelon}
      </span>
      {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-echo-yellow" />}
    </button>
  );
}

/** Unit patch (or shield fallback) at the left of a row. */
function UnitEmblem({ emblemUrl, shortName }: { emblemUrl: string | null; shortName: string }): React.JSX.Element {
  if (emblemUrl) {
    return (
      <img
        src={emblemUrl}
        alt={`${shortName} emblem`}
        className="h-8 w-8 shrink-0 rounded-md object-contain ring-1 ring-echo-border"
      />
    );
  }
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-echo-border/40 ring-1 ring-echo-border">
      <ShieldCheck className="h-4 w-4 text-echo-yellow" />
    </span>
  );
}
